import React, { useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import user from "../../../assets/user.jpg";

const DoctorDetailsForm = ({ onNext, formData, setFormData }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const defaultImageSrc = user; // fallback

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setSelectedImage(base64String);
        setFormData((prevData) => ({
          ...prevData,
          imageUpload: base64String,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const formik = useFormik({
    initialValues: {
      firstName: formData.firstName || "Dr.",
      lastName: formData.lastName || "",
      contact: formData.contact || "",
      email: formData.email || "",
      password: formData.password || "",
      confirm_Password: formData.confirm_Password || "",
    },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: Yup.object().shape({
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
      contact: Yup.string()
        .required("Phone number is required")
        .matches(/^\d{10}$/, "Must be exactly 10 digits"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      password: Yup.string()
        .min(7, "Password must be at least 7 characters")
        .max(20, "Password cannot exceed 20 characters")
        .matches(/[A-Z]/, "Must contain at least one uppercase letter")
        .matches(/[a-z]/, "Must contain at least one lowercase letter")
        .matches(/\d/, "Must contain at least one number")
        .matches(
          /[!@#$%^&*(),.?":{}|<>]/,
          "Must contain at least one special character"
        )
        .required("Password is required")
        .test(
          "no-name-in-password",
          "Password cannot contain your email username",
          function (value) {
            const nameFromEmail = this.parent.email.split("@")[0].toLowerCase();
            return !value.toLowerCase().includes(nameFromEmail);
          }
        ),
      confirm_Password: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Confirm password is required"),
    }),
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  const handleSubmit = (values) => {
    // Check for duplicate contact and email in localStorage
    const users = JSON.parse(localStorage.getItem("users")) || [];

    // Exclude current user (if editing) – but in registration we assume new
    const duplicateContact = users.find(
      (user) => user.contact === values.contact
    );
    if (duplicateContact) {
      formik.setFieldError("contact", "This phone number is already registered.");
      return;
    }

    const duplicateEmail = users.find(
      (user) => user.email === values.email
    );
    if (duplicateEmail) {
      formik.setFieldError("email", "This email is already registered.");
      return;
    }

    // Update parent formData and proceed
    setFormData((prev) => ({
      ...prev,
      ...values,
    }));

    onNext(values);
  };

  return (
    <div className="bg-gray-200 min-h-screen">
      <div className="mx-auto">
        <div className="flex justify-center md:px-6">
          <div className="w-full lg:w-7/12 p-5 rounded-lg lg:rounded-l-none">
            <form
              className="relative px-8 pt-6 pb-8 mb-4 bg-white rounded-xl"
              style={{ backgroundColor: "rgb(255,255,255)" }}
              onSubmit={formik.handleSubmit}
            >
              {/* Decorative blobs */}
              <div className="absolute top-0 left-0 w-64 h-64 bg-gradient-to-br from-teal-400 to-blue-500 rounded-full filter blur-3xl opacity-20 -translate-x-1/3 -translate-y-1/3"></div>
              <div className="absolute pointer-events-none bottom-0 right-0 w-64 h-64 bg-gradient-to-tl from-orange-400 to-pink-500 rounded-full filter blur-3xl opacity-20 translate-x-1/3 translate-y-1/3"></div>

              <div className="flex flex-col md:flex-row gap-6 px-4 pb-4">
                {/* Image Upload */}
                <div className="w-full md:w-1/5 flex justify-center md:justify-start">
                  <div className="flex flex-col items-center space-y-4">
                    <label className="block relative w-28 h-28 rounded-full border-2 border-teal-500 cursor-pointer hover:border-teal-600 transition-colors">
                      <span className="sr-only">Choose profile photo</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <div className="w-full h-full rounded-full overflow-hidden flex items-center justify-center bg-gray-100">
                        <img
                          src={selectedImage || defaultImageSrc}
                          className="w-full h-full object-cover"
                          alt="Profile"
                        />
                      </div>
                      <div className="absolute bottom-0 right-0 bg-teal-500 text-white rounded-full w-8 h-8 flex items-center justify-center border-2 border-white">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Form Fields */}
                <div className="w-full md:w-4/5">
                  <p className="text-teal-500 text-sm font-semibold">
                    BASIC INFORMATION & PASSWORD CREATION
                  </p>
                  <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 pb-2">
                    <div className="col-span-1">
                      <p className="text-gray-500">First Name</p>
                      <input
                        type="text"
                        name="firstName"
                        value={formik.values.firstName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                        placeholder="First Name"
                      />
                      {formik.touched.firstName && formik.errors.firstName && (
                        <p className="text-red-500 text-xs mt-1">
                          {formik.errors.firstName}
                        </p>
                      )}
                    </div>

                    <div className="col-span-1">
                      <p className="text-gray-500">Last Name</p>
                      <input
                        type="text"
                        name="lastName"
                        value={formik.values.lastName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                        placeholder="Last Name"
                      />
                      {formik.touched.lastName && formik.errors.lastName && (
                        <p className="text-red-500 text-xs mt-1">
                          {formik.errors.lastName}
                        </p>
                      )}
                    </div>

                    <div className="col-span-1">
                      <p className="text-gray-400">Email ID</p>
                      <input
                        type="email"
                        name="email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                        placeholder="Email"
                      />
                      {formik.touched.email && formik.errors.email && (
                        <p className="text-red-500 text-xs mt-1">
                          {formik.errors.email}
                        </p>
                      )}
                    </div>

                    <div className="col-span-1">
                      <p className="text-gray-400">Contact Number</p>
                      <input
                        type="tel"
                        inputMode="numeric"
                        name="contact"
                        value={formik.values.contact}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                        placeholder="Mobile Number"
                      />
                      {formik.touched.contact && formik.errors.contact && (
                        <p className="text-red-500 text-xs mt-1">
                          {formik.errors.contact}
                        </p>
                      )}
                    </div>

                    <div className="col-span-1">
                      <p className="text-gray-400">Create Password</p>
                      <input
                        type="password"
                        name="password"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                        placeholder="Password"
                      />
                      {formik.touched.password && formik.errors.password && (
                        <p className="text-red-500 text-xs mt-1">
                          {formik.errors.password}
                        </p>
                      )}
                    </div>

                    <div className="col-span-1">
                      <p className="text-gray-400">Retype To Confirm</p>
                      <input
                        type="password"
                        name="confirm_Password"
                        value={formik.values.confirm_Password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                        placeholder="Confirm Password"
                      />
                      {formik.touched.confirm_Password && formik.errors.confirm_Password && (
                        <p className="text-red-500 text-xs mt-1">
                          {formik.errors.confirm_Password}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <div className="mb-6 text-center">
                  <button
                    type="submit"
                    className="w-full px-4 py-2 cursor-pointer font-bold text-white bg-orange-500 rounded-full hover:bg-orange-700 focus:outline-none focus:shadow-outline"
                  >
                    Next
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorDetailsForm;